/**
 * 構造化データ（JSON-LD）生成の純粋関数。
 * 打ち上げは client 側で非同期取得するため、確定日付の打ち上げだけを schema.org/Event として吐き、
 * app.ts が <script type="application/ld+json"> に注入する（Googlebotはレンダ後のJSON-LDを拾う）。
 *
 * ★正確性の規律: 日付が粗い(Y/M/Q)打ち上げは startDate を捏造せず Event を出さない
 *   （不正確な構造化データはGoogleに嫌われる＝この案件の「推測しない」規律と同じ）。
 *
 * ★2026-07-31 の是正（3件）
 *   (a) location.address が無く、**全件がGoogleのEvent要件を満たしていなかった**。
 *       Googleは Place に address を要求する。geo だけでは足りない。
 *   (b) DAY精度（日付は確定・時刻未定）の回で `startDate` に LL2 の生netを素通ししており、
 *       `2028-04-20T00:00:00Z` ＝ **JST 09:00 という決まっていない時刻を宣言していた**。
 *       日付のみのときは日付だけを出す（schema.org は Date も許容する）。
 *   (c) DAY精度の唯一の該当が2年先の惑星探査ミッションだった。2年先の予定を
 *       EventScheduled として宣言するのは確度の過剰主張なので、地平線でゲートする。
 */

/** Event生成に必要な打ち上げの最小形（LaunchDTOが構造的にこれを満たす）。 */
export interface LaunchLike {
  siteId: string | null;
  net: string;
  netPrecision: string;
  rocket?: string | null;
  mission?: string | null;
  name?: string | null;
  windowEnd?: string | null;
}

export interface SiteGeo {
  nameJa: string;
  lat: number;
  lon: number;
  addressRegion: string;
  addressLocality: string;
}

/**
 * 日付のみ確定（DAY精度）の回を Event 化してよい上限日数。
 * これより先の「日付確定」は、実務上まだ動く前提の暫定日なので構造化データにしない。
 */
export const DAY_PRECISION_HORIZON_DAYS = 180;

/** JSTの暦日（YYYY-MM-DD）。時刻を宣言したくないときに使う。 */
function jstDateOnly(iso: string): string | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  // UTC+9 に寄せてから日付部分だけ取る
  return new Date(t + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

/**
 * 確定日付（bookable）の打ち上げのみ schema.org/Event の配列を返す。
 * @param resolveSite  siteId → 射場の名称・座標・住所（無ければ null）
 * @param isBookable   netPrecision が予約に足る確度か（format.precisionInfo().bookable を渡す）
 * @param isTimeKnown  時刻まで確定しているか（format.precisionInfo().tier === 'time' を渡す）
 * @param nowMs        地平線ゲートの基準時刻
 */
export function buildLaunchEventsJsonLd(
  launches: LaunchLike[],
  resolveSite: (siteId: string | null) => SiteGeo | null,
  isBookable: (netPrecision: string) => boolean,
  isTimeKnown: (netPrecision: string) => boolean = () => true,
  nowMs: number = Date.now(),
): object[] {
  const out: object[] = [];
  for (const l of launches) {
    if (!l.siteId || !l.net) continue;
    if (!isBookable(l.netPrecision)) continue; // 日付が粗いものは出さない（捏造しない）
    const site = resolveSite(l.siteId);
    if (!site) continue;

    const timeKnown = isTimeKnown(l.netPrecision);
    const t = Date.parse(l.net);
    if (!Number.isFinite(t)) continue;
    // 日付のみ確定の回は、遠すぎるものを EventScheduled として宣言しない
    if (!timeKnown && t - nowMs > DAY_PRECISION_HORIZON_DAYS * 86400000) continue;

    const rocket = (l.rocket || l.name?.split('|')[0] || '').trim();
    const mission = (l.mission || l.name?.split('|')[1] || '').trim();
    const base = rocket ? `${rocket}打ち上げ` : '打ち上げ';
    const name = mission ? `${base}（${mission}）` : base;

    // 時刻が確定していない回に時刻を書かない。schema.org は Date（YYYY-MM-DD）も受け付ける。
    const startDate = timeKnown ? l.net : jstDateOnly(l.net);
    if (!startDate) continue;

    out.push({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name,
      startDate,
      ...(timeKnown && l.windowEnd ? { endDate: l.windowEnd } : {}),
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: {
        '@type': 'Place',
        name: site.nameJa,
        // Google の Event 構造化データは address を必須にしている（geoだけでは不適格）
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'JP',
          addressRegion: site.addressRegion,
          addressLocality: site.addressLocality,
        },
        geo: { '@type': 'GeoCoordinates', latitude: site.lat, longitude: site.lon },
      },
    });
  }
  return out;
}

/**
 * JSON-LD を <script type="application/ld+json"> の中に置ける形へ直列化する。
 * =====================================================================
 * ★なぜ素の JSON.stringify ではだめか（2026-09-07 に天体イベントのテストで発覚）
 *   HTML の仕様上、`<script>` の中身は「`</script`」が現れた時点でそこが終わりになる。
 *   JSON.stringify は `<` を素通しするので、データに `</script>` を含む文字列が
 *   1つでもあると **スクリプト要素が途中で閉じ、以降が本文のHTMLとして解釈される**。
 *   記事の見出し・要約は管理画面（/admin）から書けるので、
 *   「管理者しか書けないから安全」ではなく**構造的に起こせなくする**。
 *
 * ★`<` を `<` に置き換えるだけでよい理由
 *   JSON の文字列としては `<` と `<` は同じ値なので、構造化データの意味は変わらない。
 *   終了タグの検出は生の `<` を見るので、これで `</script>` は成立しなくなる。
 *   （念のため `>` と `&` も同じ方式で逃がしておく。副作用はない。）
 */
export function jsonLdScriptBody(jsonLd: unknown): string {
  return JSON.stringify(jsonLd, null, 2)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}
